import React, { useMemo } from 'react';

import { Box } from '@rbilabs/components-library/build/components/layout';
import { RadioButton } from '@rbilabs/components-library/build/components/radio-button';
import { getLocales } from '@rbilabs/intl';
import { useIntl } from 'react-intl';

import ActionButton from 'components/action-button';
import { messageIdMap } from 'components/country-select/generated/country-info';
import { useLocale } from 'state/intl';
import { useFlag } from 'state/launchdarkly';
import { brand } from 'utils/environment';
import { RegionToISOs } from 'utils/form/constants';
import { LaunchDarklyFlag } from 'utils/launchdarkly';
import { formatCurrencyCodeForRegion } from 'utils/payment/format-currency-code-for-region';

import { SHOW_LANGUAGE_BEFORE_COUNTRY } from '../show-language-before-country';

import {
  Disclaimer,
  LanguageSelectionCell,
  LanguageSelectionContainer,
  ModalAdditionalContentWrapper,
} from './styled';

interface ILanguageAndCountryLabelProps {
  labelHeader: boolean;
  showLanguageBeforeCountry: boolean;
  singleRegionLanguageSelector: boolean;
  mappedLanguage: string;
  mappedRegion: string;
}

const LabelContent: React.FC<ILanguageAndCountryLabelProps> = ({
  labelHeader,
  showLanguageBeforeCountry,
  singleRegionLanguageSelector,
  mappedLanguage,
  mappedRegion,
}) => {
  const { formatMessage } = useIntl();

  const languageLabel = (
    <span lang={mappedLanguage.toLowerCase()}>
      {formatMessage({ id: `languageName.${mappedLanguage}` })}
    </span>
  );

  const regionLabel = !singleRegionLanguageSelector ? (
    <span data-testid={`dialog-code-${mappedLanguage.toLowerCase()}-${mappedRegion.toLowerCase()}`}>
      {formatCurrencyCodeForRegion(formatMessage({ id: messageIdMap[RegionToISOs[mappedRegion]] }))}
    </span>
  ) : null;

  if (showLanguageBeforeCountry) {
    return labelHeader ? languageLabel : regionLabel;
  }
  return labelHeader ? regionLabel : languageLabel;
};

interface ILanguageModalContentsProps {
  regionFilter: (region: string) => boolean;
  disclaimer?: string;
  applyButtonText: string;
  setLanguage: () => void;
  handleSelection: (
    selectedLanguage: string,
    mappedRegion: string,
    isOneStep: boolean
  ) => () => void;
  regionKey: string;
  langKey: string;
  /**
   * Pass the id of the heading that describes the radio group.
   */
  ariaRadioGroupLabeledBy: string;
}

const LanguageSelectorModalContents: React.FC<ILanguageModalContentsProps> = ({
  regionKey,
  langKey,
  disclaimer,
  applyButtonText,
  setLanguage,
  handleSelection,
  ariaRadioGroupLabeledBy = '',
}) => {
  const { formatMessage } = useIntl();
  const { region } = useLocale();
  const singleRegionLanguageSelector = useFlag(LaunchDarklyFlag.SINGLE_REGION_LANGUAGE_SELECTOR);

  const locales = useMemo(() => getLocales(brand(), region), [region]);

  const regionLanguageSelections = locales.map(locale => {
    const language = locale.slice(0, 2);
    const commonLabelProps = {
      showLanguageBeforeCountry: SHOW_LANGUAGE_BEFORE_COUNTRY,
      singleRegionLanguageSelector,
      mappedLanguage: language,
      mappedRegion: region,
    };
    return (
      <LanguageSelectionCell key={`dialog-button-${language}-${region}`}>
        <RadioButton
          value={`${language.toLowerCase()}-${region}`}
          data-testid={`dialog-button-${language.toLowerCase()}-${region.toLowerCase()}`}
          onChange={handleSelection(language, region, false)}
          checked={regionKey === region && langKey === language}
          aria-label={`${formatMessage({ id: 'select' })} ${formatMessage({
            id: `languageName.${language}`,
          })} / ${region}`}
          labelHeader={<LabelContent labelHeader {...commonLabelProps} />}
          label={<LabelContent labelHeader={false} {...commonLabelProps} />}
        />
      </LanguageSelectionCell>
    );
  });

  return (
    <>
      <LanguageSelectionContainer role="radiogroup" aria-labelledby={ariaRadioGroupLabeledBy}>
        {regionLanguageSelections}
      </LanguageSelectionContainer>
      {disclaimer && (
        <ModalAdditionalContentWrapper>
          <Disclaimer>{disclaimer}</Disclaimer>
        </ModalAdditionalContentWrapper>
      )}
      <Box center width="100%" margin="0 0 2rem">
        <ActionButton onClick={setLanguage} data-testid="action-button-apply-language-selection">
          {applyButtonText}
        </ActionButton>
      </Box>
    </>
  );
};

export default LanguageSelectorModalContents;
